import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormLabel,
  CFormSelect,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CFormInput,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import ReactPaginate from "react-js-pagination";
import { Button } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import * as Yup from "yup";
import { createCapitals, getAllCapitals } from "src/redux/actions/customerAction";
import { CSVLink } from "react-csv";
import { COMPANY_NAME, formatDate, Loader } from "src/services/const";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { updateCapitalAccountPaymentInfo } from "src/api/allApis";
import { errorAlert, successAlert } from "src/utils/Swal";
import PropTypes from "prop-types";

const TDS_RATE = 10;

const DirectorListById = ({ setDirectorData, dataByDirectorId }) => {
  const dispatch = useDispatch();
  const componentPDF = useRef();

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [loader, setLoader] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [paymentList, setPaymentList] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [allPaymentsModal, setAllPaymentsModal] = useState(false);
  const [interestDate, setInterestDate] =
    useState(
      new Date().toISOString().split("T")[0]
    );
  const SchemaName = sessionStorage.getItem("schemaName");

  const { Capitals } = useSelector((state) => state?.customerReducer);

  useEffect(() => {
    dispatch(getAllCapitals({ schemaName: SchemaName }));
  }, []);

  // ✅ Filter + Pagination optimized
  const filteredData = useMemo(() => {
    return (
      Capitals?.data
        ?.filter(
          (item) =>
            item?.directorId === dataByDirectorId?.id
        )
        ?.map((item) => ({
          ...item,
          interestTillDate: interestDate,
        })) || []
    );
  }, [
    Capitals,
    dataByDirectorId,
    interestDate
  ]);

  const currentRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const addSchema = Yup.object({
    type: Yup.string().required(),
    amount: Yup.number().required().positive(),
    loanInterestRate: Yup.number().when("type", {
      is: "loan",
      then: (schema) => schema.required(),
    }),
    loanDate: Yup.string().required(),
  });

  const calculateLoanData = (datas) => {

    // ---------------------------
    // SAFE DATE PARSING
    // ---------------------------

    const loanDate = datas?.loanDate
      ? new Date(datas.loanDate)
      : null;

    const tillDate = datas?.interestTillDate
      ? new Date(datas.interestTillDate)
      : new Date();

    // invalid loan date
    if (!loanDate || isNaN(loanDate.getTime())) {

      return {
        grossInterest: 0,
        capitalPaid: 0,
        loanPaid: 0,
        interestPaidGross: 0,
        tdsPaid: 0,
        netInterestPaid: 0,
        loanBalance: Number(datas?.loanAmount || 0),
        interestBalance: 0,
        interestDays: 0,
      };
    }

    let finalDate = tillDate;

    const payments = Array.isArray(datas?.paymentDetails)
      ? datas.paymentDetails
      : [];

    let capitalPaid = 0;
    let loanPaid = 0;
    let interestPaidGross = 0;
    let tdsPaid = 0;

    let lastLoanPaymentDate = null;

    // ---------------------------
    // PAYMENT LOOP
    // ---------------------------

    payments.forEach((p) => {

      const amount = Number(p.amount || 0);

      if (p.paymentType === "capital") {
        capitalPaid += amount;
      }

      if (p.paymentType === "loan") {

        loanPaid += amount;

        if (p.date_time) {

          const paymentDate = new Date(p.date_time);

          if (
            !isNaN(paymentDate.getTime())
          ) {

            if (
              !lastLoanPaymentDate ||
              paymentDate > lastLoanPaymentDate
            ) {
              lastLoanPaymentDate = paymentDate;
            }
          }
        }
      }

      if (p.paymentType === "interest") {

        interestPaidGross += amount;

        tdsPaid += Number(p.tds || 0);
      }
    });

    // ---------------------------
    // REMAINING LOAN
    // ---------------------------

    const loanAmount =
      Number(datas?.loanAmount || 0);

    const loanInterestRate =
      Number(datas?.loanInterestRate || 0);

    const remainingLoan =
      loanAmount - loanPaid;

    // stop interest after loan close
    if (
      remainingLoan <= 0 &&
      lastLoanPaymentDate
    ) {

      if (lastLoanPaymentDate < tillDate) {
        finalDate = lastLoanPaymentDate;
      }
    }

    // ---------------------------
    // DAYS DIFFERENCE
    // ---------------------------

    const diffTime =
      finalDate.getTime() -
      loanDate.getTime();

    const timeInDays =
      diffTime > 0
        ? Math.floor(
          diffTime /
          (1000 * 60 * 60 * 24)
        )
        : 0;

    // ---------------------------
    // INTEREST CALCULATION
    // ---------------------------

    // -----------------------------------
    // REDUCING BALANCE INTEREST
    // -----------------------------------

    let grossInterest = 0;

    let runningBalance = loanAmount;

    let previousDate = loanDate;

    // only loan payments
    const loanPayments = payments
      .filter(
        (p) =>
          p.paymentType === "loan" &&
          p.date_time
      )
      .sort(
        (a, b) =>
          new Date(a.date_time) -
          new Date(b.date_time)
      );

    // -----------------------------------
    // PERIOD WISE INTEREST
    // -----------------------------------

    loanPayments.forEach((payment) => {

      const paymentDate = new Date(
        payment.date_time
      );

      // skip future payments
      if (paymentDate > finalDate) {
        return;
      }

      // calculate days
      const diffDays = Math.floor(
        (
          paymentDate.getTime() -
          previousDate.getTime()
        ) /
        (1000 * 60 * 60 * 24)
      );

      // interest for current balance
      const periodInterest =
        (
          runningBalance *
          loanInterestRate *
          diffDays
        ) / 36500;

      grossInterest += periodInterest;

      // reduce balance after payment
      runningBalance -= Number(
        payment.amount || 0
      );

      // prevent minus balance
      if (runningBalance < 0) {
        runningBalance = 0;
      }

      // update period start
      previousDate = paymentDate;
    });

    // -----------------------------------
    // FINAL REMAINING BALANCE INTEREST
    // -----------------------------------

    if (
      runningBalance > 0 &&
      finalDate > previousDate
    ) {

      const remainingDays = Math.floor(
        (
          finalDate.getTime() -
          previousDate.getTime()
        ) /
        (1000 * 60 * 60 * 24)
      );

      const remainingInterest =
        (
          runningBalance *
          loanInterestRate *
          remainingDays
        ) / 36500;

      grossInterest += remainingInterest;
    }

    // ---------------------------
    // BALANCES
    // ---------------------------

    const loanBalance =
      loanAmount - loanPaid;

    const interestBalance =
      grossInterest - interestPaidGross;

    return {

      grossInterest:
        Number(grossInterest || 0),

      capitalPaid,
      loanPaid,
      interestPaidGross,
      tdsPaid,
      netInterestPaid:
        interestPaidGross - tdsPaid,
      loanBalance,
      interestBalance,
      interestDays: timeInDays,
      interestTillDate: finalDate,
    };
  };

  // const calculateLoanData = (datas) => {
  //   const loanDate = new Date(datas?.loanDate);
  //   const currentDate = new Date();

  //   const timeInDays = (currentDate - loanDate) / (1000 * 60 * 60 * 24);
  //   const timeInYears = timeInDays / 365;

  //   const grossInterest =
  //     (datas?.loanAmount * datas?.loanInterestRate * timeInYears) / 100;

  //   let capitalPaid = 0;
  //   let loanPaid = 0;
  //   let interestPaidGross = 0;
  //   let tdsPaid = 0;
  //   const payments = Array.isArray(datas?.paymentDetails)
  //     ? datas.paymentDetails
  //     : [];

  //   payments.forEach((p) => {
  //     if (p.paymentType === "capital") capitalPaid += p.amount;
  //     if (p.paymentType === "loan") loanPaid += p.amount;

  //     if (p.paymentType === "interest") {
  //       interestPaidGross += p.amount;   // 1000
  //       tdsPaid += p.tds || 0;           // 100
  //     }
  //   });

  //   // ✅ BALANCE CALCULATIONS
  //   const loanBalance = datas.loanAmount - loanPaid;
  //   const interestBalance = grossInterest - interestPaidGross;
  //   const advanceInterest = interestBalance < 0 ? Math.abs(interestBalance) : 0;

  //   return {
  //     grossInterest,
  //     capitalPaid,
  //     loanPaid,
  //     interestPaidGross,
  //     tdsPaid,
  //     netInterestPaid: interestPaidGross - tdsPaid,
  //     loanBalance,
  //     interestBalance,
  //   };
  // };

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        const calc = calculateLoanData(item);

        acc.totalLoanAmount += Number(item.loanAmount || 0);
        acc.totalCapitalAmount += Number(item.capital || 0);
        acc.totalInterestAmount += calc.grossInterest || 0;
        acc.totalInterestPaid += calc.interestPaidGross;
        acc.totalTdsPaid += calc.tdsPaid;
        acc.totalNetPaid += calc.netInterestPaid;
        acc.totalLoanBalance += calc.loanBalance;
        acc.totalInterestBalance += calc.interestBalance;

        return acc;
      },
      {
        totalLoanAmount: 0,
        totalCapitalAmount: 0,
        totalInterestAmount: 0,
        totalInterestPaid: 0,
        totalTdsPaid: 0,
        totalNetPaid: 0,
        totalLoanBalance: 0,
        totalInterestBalance: 0,
      }
    );
  }, [filteredData]);

  // ✅ Calculation Logic (with TDS)
  const allPayments = useMemo(() => {
    let list = [];

    filteredData.forEach((item) => {
      (item.paymentDetails || []).forEach((p) => {
        list.push({
          ...p,
          loanId: item.id,
          loanAmount: item.loanAmount,
        });
      });
    });

    return list;
  }, [filteredData]);

  // ✅ Modal open
  const handleShowAddPayDetailsForm = (details) => {
    setSelectedData(details);
    setOpenModal(true);
  };

  // ✅ Submit with TDS deduction
  const handleSubmit = async (payload) => {
    payload.id = selectedData.id;
    payload.params = { update_payment_info: 1 };

    if (payload.paymentType === "interest") {
      const tds = (payload.amount * TDS_RATE) / 100;

      payload.tds = tds;
      payload.netAmount = payload.amount - tds;

      // 🔥 IMPORTANT
      payload.grossAmount = payload.amount; // keep original
    }

    setLoader(true);
    updateCapitalAccountPaymentInfo(payload)
      .then((res) => {
        if (res.status === "success") {
          successAlert("Success", "Payment added successfully");
          dispatch(getAllCapitals({ schemaName: SchemaName }));
          setOpenModal(false);
        } else {
          errorAlert("Error", "Something went wrong");
        }
      })
      .catch(() => errorAlert("Error", "Something went wrong"))
      .finally(() => setLoader(false));
  };

  const handleViewPayments = (data) => {
    setPaymentList(data?.paymentDetails || []);
    setViewModal(true);
  };

  const handleAddSubmit = async (values) => {
    const payload = {
      directorId: dataByDirectorId.id,
      capital: values.type === "capital" ? values.amount : 0,
      loanAmount: values.type === "loan" ? values.amount : 0,
      loanInterestRate: values.loanInterestRate || 0,
      loanDate: values.loanDate,
      schemaName: SchemaName,
    };

    try {
      dispatch(createCapitals(payload));

      successAlert("Success", "Added successfully");

      setAddModal(false);
      dispatch(getAllCapitals({ schemaName: SchemaName }));

    } catch {
      errorAlert("Error", "Something went wrong");
    }
  };

  return (
    <CRow>
      <CCol xs={12}>
        <CCard>
          <CCardHeader>
            <h5>Fund Details</h5>
          </CCardHeader>

          <CCardBody>
            {/* Controls */}
            <div className="d-flex justify-content-between mb-3">
              <CFormSelect
                size="sm"
                style={{ width: "80px" }}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">50</option>
                <option value="50">100</option>
                <option value="50">200</option>
                <option value="50">500</option>
              </CFormSelect>
              <div className="d-flex align-items-center gap-2">
                <label
                  style={{
                    fontWeight: "600",
                    marginBottom: "0px",
                  }}
                >
                  Interest Till Date
                </label>

                <CFormInput
                  type="date"
                  value={interestDate}
                  onChange={(e) =>
                    setInterestDate(e.target.value)
                  }
                />

              </div>
              <div className="d-flex gap-2">
                <CButton color="dark" onClick={() => setAllPaymentsModal(true)}>
                  View All Payments
                </CButton>
                <CButton color="success" onClick={() => setAddModal(true)}>
                  Add Loan / Capital
                </CButton>
                <Button onClick={() => setDirectorData(false)}>Back</Button>
              </div>
            </div>

            {/* Table */}
            <CRow className="mb-4">
              <CCol md={3}><CCard><CCardBody><b>Total Loan</b><br />₹ {totals.totalLoanAmount.toFixed(0)}</CCardBody></CCard></CCol>
              <CCol md={3}><CCard><CCardBody><b>Total Interest</b><br />₹ {totals.totalInterestAmount.toFixed(0)}</CCardBody></CCard></CCol>
              <CCol md={3}><CCard><CCardBody><b>Total Paid</b><br />₹ {totals.totalNetPaid.toFixed(0)}</CCardBody></CCard></CCol>
              <CCol md={3}><CCard><CCardBody><b>Total TDS</b><br />₹ {totals.totalTdsPaid.toFixed(0)}</CCardBody></CCard></CCol>
            </CRow>

            <CRow className="mb-4">
              <CCol md={6}><CCard><CCardBody><b>Loan Balance</b><br />₹ {totals.totalLoanBalance.toFixed(0)}</CCardBody></CCard></CCol>
              <CCol md={6}><CCard><CCardBody><b>Interest Balance</b><br />₹ {totals.totalInterestBalance.toFixed(0)}</CCardBody></CCard></CCol>
            </CRow>
            <CTable bordered hover responsive striped>
              <CTableHead color="dark">
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Date</CTableHeaderCell>
                  <CTableHeaderCell>
                    Days
                  </CTableHeaderCell>
                  <CTableHeaderCell>Loan</CTableHeaderCell>
                  <CTableHeaderCell>Interest</CTableHeaderCell>
                  <CTableHeaderCell>Interest Paid</CTableHeaderCell>
                  <CTableHeaderCell>TDS</CTableHeaderCell>
                  <CTableHeaderCell>Net Paid</CTableHeaderCell>
                  <CTableHeaderCell>Loan Balance</CTableHeaderCell>
                  <CTableHeaderCell>Interest Balance</CTableHeaderCell>
                  <CTableHeaderCell>Action</CTableHeaderCell>
                </CTableRow>
              </CTableHead>

              <CTableBody>
                {currentRows.map((data, index) => {
                  const calc = calculateLoanData(data);

                  return (
                    <CTableRow key={index}>
                      <CTableDataCell>{index + 1}</CTableDataCell>
                      <CTableDataCell>{formatDate(data.loanDate)}</CTableDataCell>
                      <CTableDataCell>
                        {calc.interestDays}
                      </CTableDataCell>
                      <CTableDataCell>₹ {data.loanAmount}</CTableDataCell>
                      <CTableDataCell>₹ {calc.grossInterest.toFixed(2)}</CTableDataCell>

                      <CTableDataCell>₹ {calc.interestPaidGross}</CTableDataCell>
                      <CTableDataCell >
                        ₹ {calc.tdsPaid.toFixed(2)}
                      </CTableDataCell>
                      <CTableDataCell className="text-success">₹ {calc.netInterestPaid}</CTableDataCell>

                      <CTableDataCell>₹ {calc.loanBalance}</CTableDataCell>
                      <CTableDataCell>₹ {calc.interestBalance.toFixed(2)}</CTableDataCell>

                      <CTableDataCell>
                        <CButton size="sm" color="info" onClick={() => handleViewPayments(data)}>View</CButton>
                        <CButton size="sm" color="success" className="ms-2" onClick={() => handleShowAddPayDetailsForm(data)}>Add</CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}

                {/* ✅ TOTAL ROW */}
                <CTableRow style={{ fontWeight: "bold", background: "#f8f9fa" }}>
                  <CTableDataCell colSpan={2}>TOTAL</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalLoanAmount.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalInterestAmount.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalInterestPaid.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalTdsPaid.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalNetPaid.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalLoanBalance.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>₹ {totals.totalInterestBalance.toFixed(2)}</CTableDataCell>
                  <CTableDataCell>-</CTableDataCell>
                </CTableRow>
              </CTableBody>
            </CTable>

            {/* Pagination */}
            <ReactPaginate
              activePage={currentPage}
              itemsCountPerPage={rowsPerPage}
              totalItemsCount={filteredData.length}
              onChange={(page) => setCurrentPage(page)}
            />
          </CCardBody>
        </CCard>
      </CCol>
      {/* View Payments Modal */}
      <CModal visible={viewModal} onClose={() => setViewModal(false)} size="lg">
        <CModalHeader>
          <CModalTitle>Payment Details</CModalTitle>
        </CModalHeader>

        <CModalBody>
          {paymentList.length === 0 ? (
            <div className="text-center p-3">No Payments Found</div>
          ) : (
            <CTable bordered hover striped responsive>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Date</CTableHeaderCell>
                  <CTableHeaderCell>Type</CTableHeaderCell>
                  <CTableHeaderCell>Gross</CTableHeaderCell>
                  <CTableHeaderCell>TDS</CTableHeaderCell>
                  <CTableHeaderCell>Net</CTableHeaderCell>
                </CTableRow>
              </CTableHead>

              <CTableBody>
                {paymentList.map((p, i) => (
                  <CTableRow key={i}>
                    <CTableDataCell>{i + 1}</CTableDataCell>
                    <CTableDataCell>{formatDate(p.date_time)}</CTableDataCell>
                    <CTableDataCell>{p.paymentType}</CTableDataCell>
                    <CTableDataCell>₹ {p.amount}</CTableDataCell>
                    <CTableDataCell >
                      ₹ {Number(p.tds || 0).toFixed(2)}
                    </CTableDataCell>
                    <CTableDataCell className="text-success">
                      ₹ {p.netAmount || p.amount}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </CModalBody>
      </CModal>
      {/* end View Payments Modal */}

      {/* Modal */}
      <CModal visible={openModal} onClose={() => setOpenModal(false)}>
        <CModalHeader>
          <CModalTitle>Add Payment</CModalTitle>
        </CModalHeader>

        <CModalBody>
          <Formik
            initialValues={{
              paymentType: "capital",
              amount: "",
              date_time: "",
            }}
            validationSchema={Yup.object({
              paymentType: Yup.string().required(),
              amount: Yup.number().required(),
              date_time: Yup.string().required(),
            })}
            onSubmit={handleSubmit}
          >
            {({ values }) => {
              const amount = Number(values.amount || 0);
              const tds = values.paymentType === "interest" ? (amount * 10) / 100 : 0;
              const net = amount - tds;

              return (
                <Form>
                  <Field as={CFormSelect} name="paymentType">
                    <option value="capital">Capital</option>
                    <option value="loan">Loan</option>
                    <option value="interest">Interest</option>
                  </Field>

                  <Field as={CFormInput} name="amount" type="number" />
                  <Field as={CFormInput} name="date_time" type="datetime-local" />
                  {values.paymentType === "interest" && values.amount && (
                    <div
                      style={{
                        background: "#f8f9fa",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    >
                      <div className="d-flex justify-content-between">
                        <span>Gross Amount:</span>
                        <b>₹ {amount.toFixed(2)}</b>
                      </div>

                      <div className="d-flex justify-content-between ">
                        <span>TDS (10%):</span>
                        <b>₹ {tds.toFixed(2)}</b>
                      </div>

                      <div className="d-flex justify-content-between text-success">
                        <span>Net Payable:</span>
                        <b>₹ {net.toFixed(2)}</b>
                      </div>
                    </div>
                  )}
                  <small className="text-muted">
                    TDS will be deducted as per rules (10%)
                  </small>
                  {loader ? (
                    <Loader />
                  ) : (
                    <CButton type="submit">Save</CButton>
                  )}
                </Form>
              );
            }}
          </Formik>
        </CModalBody>
      </CModal>

      <CModal visible={addModal} onClose={() => setAddModal(false)}>
        <CModalHeader>
          <CModalTitle>Add Loan / Capital</CModalTitle>
        </CModalHeader>

        <CModalBody>
          <Formik
            initialValues={{
              type: "loan",
              amount: "",
              loanInterestRate: "",
              loanDate: "",
            }}
            validationSchema={addSchema}
            onSubmit={handleAddSubmit}
          >
            {({ values }) => (
              <Form className="d-flex flex-column gap-3">

                <div>
                  <CFormLabel>Type</CFormLabel>
                  <Field as={CFormSelect} name="type">
                    <option value="loan">Loan</option>
                    <option value="capital">Capital</option>
                  </Field>
                </div>

                <div>
                  <CFormLabel>Amount</CFormLabel>
                  <Field as={CFormInput} name="amount" type="number" />
                  <ErrorMessage name="amount" component="div" />
                </div>

                {values.type === "loan" && (
                  <>
                    <div>
                      <CFormLabel>Interest Rate (%)</CFormLabel>
                      <Field as={CFormInput} name="loanInterestRate" type="number" />
                    </div>

                    <div>
                      <CFormLabel>Loan Date</CFormLabel>
                      <Field as={CFormInput} name="loanDate" type="date" />
                    </div>
                  </>
                )}

                {values.type === "capital" && (
                  <div>
                    <CFormLabel>Date</CFormLabel>
                    <Field as={CFormInput} name="loanDate" type="date" />
                  </div>
                )}

                <div className="text-end">
                  <CButton type="submit" color="success">
                    Save
                  </CButton>
                </div>

              </Form>
            )}
          </Formik>
        </CModalBody>
      </CModal>

      <CModal visible={allPaymentsModal} onClose={() => setAllPaymentsModal(false)} size="xl">
        <CModalHeader>
          <CModalTitle>All Payments</CModalTitle>
        </CModalHeader>

        <CModalBody>
          {allPayments.length === 0 ? (
            <div className="text-center p-3">No Payments Found</div>
          ) : (
            <CTable bordered hover striped responsive>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>
                  <CTableHeaderCell>Date</CTableHeaderCell>
                  <CTableHeaderCell>Type</CTableHeaderCell>
                  <CTableHeaderCell>Loan ID</CTableHeaderCell>
                  <CTableHeaderCell>Gross</CTableHeaderCell>
                  <CTableHeaderCell>TDS</CTableHeaderCell>
                  <CTableHeaderCell>Net</CTableHeaderCell>
                </CTableRow>
              </CTableHead>

              <CTableBody>
                {allPayments.map((p, i) => (
                  <CTableRow key={i}>
                    <CTableDataCell>{i + 1}</CTableDataCell>
                    <CTableDataCell>{formatDate(p.date_time)}</CTableDataCell>
                    <CTableDataCell>{p.paymentType}</CTableDataCell>
                    <CTableDataCell>{p.loanId}</CTableDataCell>

                    <CTableDataCell>
                      ₹ {Number(p.amount || 0).toFixed(2)}
                    </CTableDataCell>

                    <CTableDataCell>
                      ₹ {Number(p.tds || 0).toFixed(2)}
                    </CTableDataCell>

                    <CTableDataCell className="text-success">
                      ₹ {Number(p.netAmount || (p.amount - (p.tds || 0))).toFixed(2)}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </CModalBody>
      </CModal>
    </CRow>
  );
};


DirectorListById.propTypes = {
  setDirectorData: PropTypes.func.isRequired,
  dataByDirectorId: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

export default DirectorListById;
